import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Card, CardContent, CircularProgress, Chip, List, ListItem, ListItemText } from '@mui/material';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, AreaChart, Area } from 'recharts';
import logo from "../../assets/UpdatedMainLogo.png";
import getApiBase from "../../components/getApiBase";

// Header component
const Header = ({ onLogout }) => {
  const navigate = useNavigate();
  
  return (
    <header style={{
      backgroundColor: '#fff',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      padding: '10px 20px',
      position: 'sticky',
      top: 0,
      zIndex: 1000
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <img src={logo} alt="Autoshops.com Logo" style={{ height: 'auto', width: '300px' }} />
        </Box>
        <nav style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <a 
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin');
            }}
            style={{ 
              color: '#ea4335', 
              textDecoration: 'none', 
              fontFamily: 'Poppins, sans-serif', 
              fontWeight: 700,
              fontSize: '18px',
              padding: '10px',
              borderRadius: '8px',
              transition: 'background-color 0.2s ease, color 0.2s ease',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#e63926';
              e.target.style.color = '#fff';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#ea4335';
            }}
          >
            ← Back to Dashboard
          </a>
          <span style={{ 
            color: '#000', 
            fontFamily: 'Poppins, sans-serif', 
            fontWeight: 700, 
            fontSize: '18px',
            padding: '10px' 
          }}>
            Price Management
          </span>
          <Button
            onClick={onLogout}
            variant="contained"
            sx={{
              backgroundColor: '#4285f4',
              color: 'white',
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '16px',
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#1b449e',
                transform: 'translateY(-1px)'
              }
            }}
          >
            Logout
          </Button>
        </nav>
      </Box>
    </header>
  );
};

// Compact Stat Card
const StatCard = ({ title, count, icon, bgColor, onClick, loading }) => {
  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2
        },
        backgroundColor: bgColor,
        color: '#fff',
        borderRadius: 2,
        boxShadow: 'none',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {loading ? (
          <CircularProgress size={24} sx={{ color: '#fff' }} />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500, display: 'block', mb: 0.5 }}>
                {title}
              </Typography>
              <Typography variant="h4" sx={{ 
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif'
              }}>
                {count}
              </Typography>
            </Box>
            <Typography variant="h3" sx={{ opacity: 0.9 }}>{icon}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Plan List Card
const PlanListCard = ({ title, plans, onClick }) => {
  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 2
        },
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid #e5e7eb',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="body2" sx={{ 
          fontWeight: 600, 
          color: '#374151',
          fontFamily: 'Poppins, sans-serif',
          mb: 1.5
        }}>
          {title}
        </Typography>
        <List sx={{ p: 0 }}>
          {plans.map((plan, index) => (
            <ListItem 
              key={index} 
              sx={{ 
                px: 0,
                py: 0.75,
                minHeight: 'auto',
                borderBottom: index < plans.length - 1 ? '1px solid #f3f4f6' : 'none'
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: plan.color }}>
                      ${plan.price}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.25 }}>
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>
                      {plan.type}
                    </Typography>
                    <Chip 
                      label={plan.status} 
                      size="small"
                      sx={{ 
                        backgroundColor: plan.statusColor + '15',
                        color: plan.statusColor,
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: '18px'
                      }}
                    />
                  </Box>
                }
                sx={{ my: 0 }}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

// Chart Card
const ChartCard = ({ title, data, color, onClick, loading, type = "area" }) => {
  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 2
        },
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid #e5e7eb',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="body2" sx={{ 
          fontWeight: 600, 
          color: '#374151',
          fontFamily: 'Poppins, sans-serif',
          mb: 1.5
        }}>
          {title}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '140px' }}>
            <CircularProgress size={32} sx={{ color }} />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={140}>
            {type === "bar" ? (
              <BarChart data={data}>
                <XAxis dataKey="name" stroke="#9ca3af" style={{ fontSize: '10px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '10px' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '6px',
                    fontSize: '11px'
                  }}
                />
                <Bar dataKey="value" fill={color} radius={[6, 6, 0, 0]} />
              </BarChart>
            ) : (
              <AreaChart data={data}>
                <defs>
                  <linearGradient id={`price-grad-${color}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={color} stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" stroke="#9ca3af" style={{ fontSize: '10px' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '6px',
                    fontSize: '11px'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke={color} 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill={`url(#price-grad-${color})`} 
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Action Card
const ActionCard = ({ title, subtitle, icon, bgColor, onClick }) => {
  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2
        },
        backgroundColor: bgColor,
        color: '#fff',
        borderRadius: 2,
        boxShadow: 'none',
        height: '100%'
      }}
    >
      <CardContent sx={{ 
        p: 2.5,
        '&:last-child': { pb: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%'
      }}>
        <Typography variant="h2" sx={{ mb: 2, opacity: 0.9 }}>
          {icon}
        </Typography>
        <Box>
          <Typography variant="h6" sx={{ 
            fontWeight: 700,
            fontFamily: 'Poppins, sans-serif',
            mb: 0.5
          }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {subtitle}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const PricingDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPlans: 0,
    activePlans: 0,
    bidTiers: 0,
    addons: 0
  });

  useEffect(() => {
    checkAuth();
    fetchStats();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${getApiBase()}/api/auth/me`, {
        credentials: "include",
      });

      if (!response.ok) {
        navigate("/admin");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      navigate("/admin");
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      const plansResponse = await fetch(`${getApiBase()}/api/admin/subscription-plans`, {
        credentials: 'include'
      });
      
      if (plansResponse.ok) {
        const data = await plansResponse.json();
        const plans = data.plans || [];
        
        setStats({
          totalPlans: plans.length,
          activePlans: plans.filter(p => p.is_active === 1).length,
          bidTiers: plans.filter(p => p.plan_type === 'bid_tier').length,
          addons: plans.filter(p => p.plan_type === 'addon').length
        });
      }

    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${getApiBase()}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      localStorage.removeItem("user");
      navigate('/login');
    } catch (err) {
      console.error("Logout failed:", err);
      navigate('/login');
    }
  };

  const revenueData = [
    { name: 'Jan', value: 3200 },
    { name: 'Feb', value: 4100 },
    { name: 'Mar', value: 3800 },
    { name: 'Apr', value: 5200 },
    { name: 'May', value: 6100 },
    { name: 'Jun', value: 5900 }
  ];

  const planPricingData = [
    { name: '3 Bids', value: 14.99 },
    { name: '10 Bids', value: 39.99 },
    { name: '25 Bids', value: 69.99 },
    { name: 'Unlimited', value: 99.99 }
  ];

  const topPlans = [
    { name: 'Unlimited Bids', price: '99.99', type: 'Bid Tier', status: 'Active', statusColor: '#059669', color: '#374151' },
    { name: '25 Bids/Month', price: '69.99', type: 'Bid Tier', status: 'Active', statusColor: '#059669', color: '#4285f4' },
    { name: '10 Bids/Month', price: '39.99', type: 'Bid Tier', status: 'Active', statusColor: '#059669', color: '#e63926' },
    { name: 'Priority Support', price: '49.99', type: 'Add-on', status: 'Active', statusColor: '#059669', color: '#f59e0b' }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <Header onLogout={handleLogout} />
      
      <Box sx={{ p: 3, maxWidth: '1600px', mx: 'auto' }}>
        {/* Top Stats */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 2,
          mb: 2
        }}>
          <StatCard
            title="Total Plans"
            count={stats.totalPlans}
            icon="📋"
            bgColor="#374151"
            onClick={() => navigate('/admin/pricing')}
            loading={loading}
          />
          <StatCard
            title="Active Plans"
            count={stats.activePlans}
            icon="✅"
            bgColor="#059669"
            onClick={() => navigate('/admin/pricing?filter=active')}
            loading={loading}
          />
          <StatCard
            title="Bid Tiers"
            count={stats.bidTiers}
            icon="🎯"
            bgColor="#4285f4"
            onClick={() => navigate('/admin/pricing')}
            loading={loading}
          />
          <StatCard
            title="Add-ons"
            count={stats.addons}
            icon="⭐"
            bgColor="#e63926"
            onClick={() => navigate('/admin/pricing')}
            loading={loading}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/admin/platform-fee')}
            sx={{ backgroundColor: '#374151' }}
          >
            Platform fee rate (TASK-567)
          </Button>
        </Box>

        {/* Mixed Grid */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: 2,
          gridAutoRows: '180px'
        }}>
          {/* Manage Plans Action - 2 rows, 4 cols */}
          <Box sx={{ gridColumn: 'span 4', gridRow: 'span 2' }}>
            <ActionCard
              title="Manage Plans"
              subtitle="Edit, create, and configure all subscription plans"
              icon="📝"
              bgColor="#4285f4"
              onClick={() => navigate('/admin/pricing')}
            />
          </Box>

          {/* Revenue Chart - 2 rows, 5 cols */}
          <Box sx={{ gridColumn: 'span 5', gridRow: 'span 2' }}>
            <ChartCard
              title="Revenue Trend (Mock)"
              data={revenueData}
              color="#e63926"
              onClick={() => alert('Revenue analytics coming soon!')}
              loading={false}
              type="area"
            />
          </Box>

          {/* Top Plans List - 2 rows, 3 cols */}
          <Box sx={{ gridColumn: 'span 3', gridRow: 'span 2' }}>
            <PlanListCard
              title="Top Plans"
              plans={topPlans}
              onClick={() => navigate('/admin/pricing')}
            />
          </Box>

          {/* Plan Pricing Chart - 1 row, 12 cols */}
          <Box sx={{ gridColumn: 'span 12', gridRow: 'span 1' }}>
            <ChartCard
              title="Plan Pricing Overview"
              data={planPricingData}
              color="#4285f4"
              onClick={() => navigate('/admin/pricing')}
              loading={loading}
              type="bar"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PricingDashboard;
